import React, {Component} from 'react';
import PropTypes from 'prop-types';

import SimpleSpinner from '~/components/SimpleSpinner';

import {getViewImage, getViewImageWithUri} from '~/utils/catalog';
import {getProductIdViewNameKey, isSleeveView} from '~/utils/product';
import {imageUrl} from '~/utils/productImage';
import {
  GUN_METAL_WATCH_ID,
  ROSE_WATCH_ID,
  SPORTS_WATCH_ID
} from '~/constants/ModifyWatchConstants';
import MediaQueries, {breakpoint} from '~/constants/MediaQueries';
import {
  ALT_IMAGERY_BLACKLIST,
  DESKTOP_IMAGE_WIDTH,
  ID_VIEW_MAP,
  MOBILE_IMAGE_WIDTH
} from '~/constants/ProductConstants';

import Checkerboard from '~/assets/images/checkerboard.png';
import RoseWatch from '~/assets/images/RoseWatch.png';
import SportWatch from '~/assets/images/SportWatch.png';
import GunmetalWatch from '~/assets/images/GunmetalWatch.png';

const invalidAltImages = {};

function isAltEnabled(props) {
  const {styleId} = props;
  const productViewKey = getProductIdViewNameKey({
    productId: props.productId,
    viewName: props.viewName
  });
  if (!productViewKey) return true;
  const productEnabled = !invalidAltImages[productViewKey];
  const styleEnabled = styleId && !ALT_IMAGERY_BLACKLIST[styleId];
  return productEnabled && styleEnabled;
}

const propTypes = {
  productId: PropTypes.string.isRequired,
  styleId: PropTypes.string.isRequired,
  altImagePath: PropTypes.string,
  categoryIds: PropTypes.array,
  className: PropTypes.string,
  height: PropTypes.number,
  imagePath: PropTypes.string,
  isSleeveDecoEligible: PropTypes.bool,
  product: PropTypes.object,
  showLoader: PropTypes.bool,
  showOverlay: PropTypes.bool,
  similarProduct: PropTypes.object,
  style: PropTypes.object,
  viewId: PropTypes.number,
  viewName: PropTypes.string,
  width: PropTypes.number,
  onLoad: PropTypes.func
};

/** @extends {Component<PropTypes.InferProps<typeof propTypes>>} */
export class ProductImage extends Component {
  static displayName = 'ProductImage';
  static propTypes = propTypes;

  state = {
    altIsEnabled: false,
    loaded: false,
    showViewName: false,
    useImgix: true,
    displayEmptyProductImage: {}
  };

  UNSAFE_componentWillMount() {
    this.setState({altIsEnabled: isAltEnabled(this.props)});
    this.setStartTime();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState({altIsEnabled: isAltEnabled(nextProps)});
    if (
      (this.state.altIsEnabled &&
        nextProps.altImagePath !== this.props.altImagePath) ||
      nextProps.imagePath !== this.props.imagePath
    ) {
      this.setStartTime();
      this.setState({loaded: false});
    }
  }

  componentWillUnmount() {
    if (this.loadTimer) {
      clearTimeout(this.loadTimer);
    }
  }

  startTime = null;

  setStartTime() {
    this.startTime = Date.now();
  }

  handleLoad = event => {
    this.setState({loaded: true, showViewName: true});
    this.loadTimer = setTimeout(
      () => this.setState({showViewName: false}),
      1000
    );
    this.props.onLoad && this.props.onLoad(event);
  };

  handleError = () => {
    const {productId, viewName} = this.props;
    const {altIsEnabled, useImgix} = this.state;
    const productIdViewNameKey = getProductIdViewNameKey({productId, viewName});

    // Try fetching image from mms-images.out.customink.com/mms
    // in case IMGIX are having problems on their side
    if (useImgix) {
      this.setState({useImgix: false});
    } else if (altIsEnabled) {
      invalidAltImages[productIdViewNameKey] = true;
      this.setState({altIsEnabled: false});
    } else {
      this.setState({
        displayEmptyProductImage: {
          [productIdViewNameKey]: true
        }
      });
    }
  };

  overlaySourceMapper = () => {
    const {categoryIds} = this.props;
    if (categoryIds) {
      if (categoryIds.includes(ROSE_WATCH_ID)) {
        return RoseWatch;
      }
      if (categoryIds.includes(GUN_METAL_WATCH_ID)) {
        return GunmetalWatch;
      }
      if (categoryIds.includes(SPORTS_WATCH_ID)) {
        return SportWatch;
      }
    }
  };

  getOverlayMetadata = () =>
    // We should be passing in the offset of the thing here.  Its the OG offset + scaled x,y from mms
    ({
      src: this.overlaySourceMapper(),
      style: {
        ...this.props.style,
        position: 'absolute',
        zIndex: 1,
        pointerEvents: 'none',
        opacity: this.props.showOverlay ? 100 : 0
      }
    });

  getImagePath = () => {
    const {altIsEnabled} = this.state;
    const {
      altImagePath,
      imagePath,
      isSleeveDecoEligible,
      product,
      similarProduct,
      viewId,
      viewName
    } = this.props;
    const givenPath = altIsEnabled && altImagePath ? altImagePath : imagePath;

    if (givenPath) return givenPath;
    if (!product) return null;

    const currentViewName = viewName || ID_VIEW_MAP[viewId];
    let viewImage = getViewImage(product, currentViewName);

    if (
      isSleeveDecoEligible &&
      isSleeveView({viewName: this.props.viewName}) &&
      similarProduct &&
      !viewImage
    ) {
      viewImage = getViewImageWithUri(similarProduct, currentViewName);
    }
    if (!viewImage) return null;

    return altIsEnabled && viewImage.alt_uri
      ? viewImage.alt_uri
      : viewImage.uri;
  };

  getImageWidth = () =>
    breakpoint(MediaQueries.mediumAndAbove)
      ? {w: DESKTOP_IMAGE_WIDTH}
      : {w: MOBILE_IMAGE_WIDTH};

  render() {
    const {productId, showLoader, viewName, ...others} = this.props;
    const {useImgix} = this.state;
    const overlayMockMetadata = this.getOverlayMetadata();
    const src = imageUrl(this.getImagePath(), useImgix, this.getImageWidth());
    const showEmptyProductImage =
      this.state.displayEmptyProductImage[
        getProductIdViewNameKey({productId, viewName})
      ];

    delete others.altImagePath;
    delete others.categoryIds;
    delete others.imagePath;
    delete others.isSleeveDecoEligible;
    delete others.onLoad;
    delete others.product;
    delete others.similarProduct;
    delete others.styleId;
    delete others.showOverlay;
    delete others.viewId;
    delete others.viewName;

    return (
      <span style={{width: '100%', height: '100%'}}>
        <img
          className="watch-overlay"
          src={overlayMockMetadata.src}
          style={overlayMockMetadata.style}
        />
        {showEmptyProductImage ? (
          <div className="no-product-image">
            <img
              alt={`failed to load view of product ${productId}`}
              draggable="false"
              src={Checkerboard}
              onLoad={this.handleLoad}
              {...others}
            />
          </div>
        ) : (
          <img
            alt={`view of product ${productId}`}
            draggable="false"
            src={src}
            onError={this.handleError}
            onLoad={this.handleLoad}
            {...others}
          />
        )}
        {showLoader && !this.state.loaded && <SimpleSpinner />}
      </span>
    );
  }
}

export default ProductImage;
